import { FieldError, Flex } from "@wayflyer/flyui";
import { RhfAutocompleteField } from "@wayflyer/flyui-hook-form";
import { IconMapPin16Line } from "@wayflyer/flyui-icons/16/line";
import { useCallback, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";

import { ManualEntryModal } from "./manual-entry-modal";
import type { EditableAddressSearchFieldProps } from "./types";
import { useEditableAddressSearch } from "./use-editable-address-search";

export const EditableAddressSearchField = ({
  form,
  handleAddressSelect,
  helperText,
  label = "Address finder",
  placeholder = "Search for address",
  fieldName = "address_search",
  clearable = false,
  countries,
  states,
  defaultCountry,
  parentFormFieldName,
  onBlur,
  onAddressChange,
  fieldMapping,
  lockFields,
  notification,
  disabled,
  errorMessage,
  onModalSave,
}: EditableAddressSearchFieldProps) => {
  const isOpeningManualEntryRef = useRef(false);

  const handleManualEntryClick = () => {
    isOpeningManualEntryRef.current = true;
    handleManualEntry(true);
  };

  const {
    addressAutocomplete,
    transformedOptions,
    handleAddressChange: internalHandleAddressChange,
    isManualEntryModalOpen,
    isProcessingSelectionRef,
    manualEntryAddress,
    handleManualEntry,
    handleModalSave,
    handleModalCancel,
    globalActionConfig,
  } = useEditableAddressSearch({
    form,
    handleAddressSelect,
    fieldName,
    countries,
    states,
    defaultCountry,
    onManualEntry: handleManualEntryClick,
    fieldMapping,
    parentFormFieldName,
  });

  const handleAutocompleteBlur = () => {
    // Don't run blur logic while a selection is in flight or the modal is open.
    // Reset the processing ref here too so it doesn't get stuck on fetch failures.
    if (isOpeningManualEntryRef.current || isManualEntryModalOpen || isProcessingSelectionRef.current) {
      isOpeningManualEntryRef.current = false;
      isProcessingSelectionRef.current = false;
      return;
    }

    const searchTerm = addressAutocomplete.searchTerm;
    if (!isOptionId && searchTerm) {
      form.setValue(fieldName, searchTerm, { shouldDirty: true });
      form.clearErrors(fieldName);
    }
    onBlur?.();
  };

  const handleAddressChange = useCallback(
    (optionId: string | number | null) => {
      if (optionId === null) {
        const currentSearchValue = form.getValues(fieldName);
        if (currentSearchValue) {
          internalHandleAddressChange(optionId);
          onAddressChange?.(optionId);
        } else {
          form.setValue(fieldName, "", { shouldDirty: true });
          addressAutocomplete.clear();
          if (fieldMapping) {
            form.setValue(fieldMapping.address_line_1, "", { shouldDirty: true });
            form.setValue(fieldMapping.address_line_2, "", { shouldDirty: true });
            form.setValue(fieldMapping.city, "", { shouldDirty: true });
            form.setValue(fieldMapping.state, "", { shouldDirty: true });
            form.setValue(fieldMapping.zip_code, "", { shouldDirty: true });
            form.setValue(fieldMapping.country_code, "", { shouldDirty: true });
          }
          if (parentFormFieldName) {
            form.resetField(parentFormFieldName);
          }
        }
        return;
      }

      internalHandleAddressChange(optionId);
      onAddressChange?.(optionId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalHandleAddressChange, onAddressChange, fieldName, fieldMapping, parentFormFieldName]
  );

  const formFieldValue = useWatch({ control: form.control, name: fieldName });

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const isOptionId = useMemo(() => {
    if (!formFieldValue || typeof formFieldValue !== "string") {
      return false;
    }
    return transformedOptions.some((opt) => opt.id === formFieldValue);
  }, [transformedOptions, formFieldValue]);

  const optionsWithManualEntry = useMemo(() => {
    if (!formFieldValue || typeof formFieldValue !== "string" || isOptionId) {
      return transformedOptions;
    }

    return [
      {
        id: formFieldValue,
        text: formFieldValue,
        subText:
          addressAutocomplete.selectedAddress?.city && addressAutocomplete.selectedAddress?.state
            ? `${addressAutocomplete.selectedAddress.city}, ${addressAutocomplete.selectedAddress.state}`
            : "",
        icon: <IconMapPin16Line />,
      },
      ...transformedOptions,
    ];
  }, [transformedOptions, formFieldValue, isOptionId, addressAutocomplete.selectedAddress]);

  const autocompleteFieldProps = {
    form,
    name: fieldName,
    label,
    helperText,
    placeholder,
    options: optionsWithManualEntry,
    externalOptions: true,
    fetching: addressAutocomplete.fetching,
    onSearchTermChange: addressAutocomplete.onSearchTermChange,
    onChange: handleAddressChange,
    onBlur: handleAutocompleteBlur,
    size: "large" as const,
    globalAction: disabled ? undefined : globalActionConfig,
    allowsEmptyCollection: true,
    disabled,
  };

  return (
    <>
      <Flex direction="column" gap="2" align="flex-start" minWidth="100%" maxWidth="100%">
        {clearable ? (
          <RhfAutocompleteField {...autocompleteFieldProps} clearable />
        ) : (
          <RhfAutocompleteField {...autocompleteFieldProps} />
        )}
        {errorMessage && <FieldError error={errorMessage} name={fieldName} />}
      </Flex>

      <ManualEntryModal
        isOpen={isManualEntryModalOpen}
        onSave={(values) => {
          handleModalSave(values);
          onModalSave?.();
        }}
        onCancel={handleModalCancel}
        initialAddress={manualEntryAddress}
        countries={countries}
        lockFields={lockFields}
        notification={notification}
      />
    </>
  );
};
